import { RaceStatus } from '../../../queries/meetingRaces';
import { HistoryRow } from '../../../queries/meetingAnalysis';

export type RaceStatusKind = 'not-analysed' | 'awaiting-results' | 'settled';

export interface RaceStatusBadge {
  kind: RaceStatusKind;
  label: string;
}

/** Derives the at-a-glance status badge for a race row from its races-status entry (or its absence). */
export function getRaceStatusBadge(status: RaceStatus | undefined): RaceStatusBadge {
  if (!status || status.tips_available === 0) {
    return { kind: 'not-analysed', label: 'Not analysed' };
  }
  if (status.results_complete) {
    return { kind: 'settled', label: 'Settled' };
  }
  return { kind: 'awaiting-results', label: `${status.results_entered}/${status.tips_available} results` };
}

export const RACE_STATUS_BADGE_CLASS: Record<RaceStatusKind, string> = {
  'not-analysed': 'bg-muted text-muted-foreground',
  'awaiting-results': 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  settled: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
};

// ponytail: date-fns isn't a dependency of admin-react, matching meetings-helpers.ts's
// precedent — use the native Intl formatter instead of pulling it in for one format call.
const TIME_FORMATTER = new Intl.DateTimeFormat('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });

export function formatRaceTime(raceTime: string | null): string {
  if (!raceTime) return '—';
  const date = new Date(raceTime);
  return Number.isNaN(date.getTime()) ? '—' : TIME_FORMATTER.format(date);
}

const HISTORY_DATE_FORMATTER = new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
const HISTORY_WEEKDAY_FORMATTER = new Intl.DateTimeFormat('en-AU', { weekday: 'long' });

export function formatHistoryDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return Number.isNaN(date.getTime()) ? '—' : HISTORY_DATE_FORMATTER.format(date);
}

/** Day of the week for a history/meeting date, e.g. "Saturday" (empty if unparseable). */
export function formatHistoryWeekday(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return Number.isNaN(date.getTime()) ? '' : HISTORY_WEEKDAY_FORMATTER.format(date);
}

const metric = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Ranks two history rows to pick a day's winning model. Tie-break order (each
 * only decides when the ones above it are equal): top-pick winners → top-pick
 * ROI → total winners → value winners → value ROI. Returns <0 when `a` is the
 * better model (so it sorts first).
 */
export function compareHistoryRows(a: HistoryRow, b: HistoryRow): number {
  return (
    metric(b.top_1_winners) - metric(a.top_1_winners) ||
    metric(b.top_1_roi) - metric(a.top_1_roi) ||
    metric(b.all_picks_winners) - metric(a.all_picks_winners) ||
    metric(b.value_winners) - metric(a.value_winners) ||
    metric(b.value_roi) - metric(a.value_roi)
  );
}

export interface HistoryDayGroup {
  meetingId: string;
  date: string | null;
  rows: HistoryRow[]; // every model that ran that day, best-first
  winner: HistoryRow; // the winning model by compareHistoryRows
  contested: boolean; // more than one model competed
}

/** Groups history rows by meeting/day, most recent first, with the winning model resolved per day. */
export function groupHistoryByDay(rows: HistoryRow[]): HistoryDayGroup[] {
  const byMeeting = new Map<string, HistoryRow[]>();
  for (const r of rows) {
    const arr = byMeeting.get(r.meeting_id);
    if (arr) arr.push(r);
    else byMeeting.set(r.meeting_id, [r]);
  }
  const groups = Array.from(byMeeting.values()).map((rs): HistoryDayGroup => {
    const sorted = [...rs].sort(compareHistoryRows);
    return { meetingId: sorted[0].meeting_id, date: sorted[0].meeting_date, rows: sorted, winner: sorted[0], contested: rs.length > 1 };
  });
  groups.sort((a, b) => String(b.date ?? '').localeCompare(String(a.date ?? '')));
  return groups;
}
